/* eslint-disable react/no-unused-state */
// Uses internal Who's on First data to do location autocomplete
import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import gettext from 'js-utils/gettext';
import {
    debounce,
    find,
    cloneDeep,
} from 'lodash';

import AutocompleteField from './AutocompleteFieldWithFallback';

import {addCheckToSuggestion} from '../utils/suggestions';
import {
    LOCATION_SUGGESTION_PROPTYPE,
    ONLINE_EVENTS_LOCATION_SLUG,
} from '../constants';

import './LocationAutocomplete.scss';

const LOCATION_DEBOUNCE_INTERVAL = 200;
const BBOX_SEARCH_DISPLAY_STRING = gettext('map area');

export default class LocationAutocompleteInternal extends PureComponent {
    static propTypes = {
        currentPlace: PropTypes.string,
        locationSuggestions: LOCATION_SUGGESTION_PROPTYPE,
        placeId: PropTypes.string,
        placeholder: PropTypes.string,
        onComponentDidMount: PropTypes.func,
        onComponentWillUnmount: PropTypes.func,
        handleUseCurrentLocation: PropTypes.func,
        handleLocationSuggestionChange: PropTypes.func,
        handleLocationSelect: PropTypes.func,
        isBBoxSearch: PropTypes.bool,
        isUseCurrentLocationEnabled: PropTypes.bool,
        isOnlineEventsEnabled: PropTypes.bool,
        isOnline: PropTypes.bool,
        isWaitingForLocation: PropTypes.bool,
        isUsingCurrentLocation: PropTypes.bool,
        disabled: PropTypes.bool,
        autocompleteProps: PropTypes.shape({
            borderType: PropTypes.string,
            id: PropTypes.string,
            name: PropTypes.string,
            hideOutline: PropTypes.bool,
            isLimitedSearch: PropTypes.bool,
            preventInputValuePrefill: PropTypes.bool,
        }),
    }

    static defaultProps = {
        autocompleteProps: {
            borderType: 'none',
            id: 'locationPicker',
            name: 'locationPicker',
            hideOutline: true,
            isLimitedSearch: true,
            preventInputValuePrefill: true,
        },
    }

    constructor(props) {
        super(props);

        const {
            currentPlace,
            isBBoxSearch,
            locationSuggestions: {
                suggestions,
            },
        } = props;

        let locationQuery = currentPlace;

        if (isBBoxSearch) {
            locationQuery = BBOX_SEARCH_DISPLAY_STRING;
        }

        this.state = {
            locationQuery,
            suggestions,
            isTyping: false,
            selectedLocation: currentPlace,
        };

    }

    componentDidMount() {
        const {onComponentDidMount} = this.props;

        if (onComponentDidMount) {
            onComponentDidMount();
        }

        this._isItemSelected = false;
    }

    componentWillReceiveProps({
        currentPlace,
        isBBoxSearch,
        locationSuggestions: {
            suggestions,
        },
        isUseCurrentLocationEnabled,
    }) {
        let newLocationQuery = currentPlace;

        if (isBBoxSearch) {
            newLocationQuery = BBOX_SEARCH_DISPLAY_STRING;
        }

        this.setState(({
            isTyping,
            locationQuery: currentLocationQuery,
            selectedLocation: currentSelectedLocation,
        }) => {
            let locationQuery = currentLocationQuery;

            let selectedLocation = currentSelectedLocation;
            const {isUsingCurrentLocation} = this.props;
            const _suggestions = cloneDeep(suggestions);

            if (isUseCurrentLocationEnabled && isUsingCurrentLocation) {
                _suggestions[0].content = addCheckToSuggestion({item: _suggestions[0], shouldShowCheck: isUsingCurrentLocation});
                _suggestions[0].secondaryContent = null;
            }

            if (!isTyping) {
                locationQuery = newLocationQuery;
                selectedLocation = newLocationQuery;
            }

            return {locationQuery, selectedLocation, suggestions: _suggestions};
        });
    }

    componentWillUnmount() {
        const {onComponentWillUnmount} = this.props;

        if (onComponentWillUnmount) {
            onComponentWillUnmount();
        }
    }

    _handleLocationAutocompleteSelect = (selectedValue) => {
        const {
            locationSuggestions: {
                suggestions,
            },
            handleLocationSelect,
            handleUseCurrentLocation,
            isUseCurrentLocationEnabled,
        } = this.props;
        const suggestedLocation = find(suggestions, ({value}) => value === selectedValue);

        this._isItemSelected = true;

        /*
            Due to a bug relating to safari's usage of back-forward cache, the resting
            javascript is executed after clicking submit and the user clicks back via the
            back button. This causes _handleLocationAutocompleteSelect to then be called with null values,
            triggering a JS error making the field totaly uninteractable. So we need
            to check that suggestedLocation exists before running this.
        */
        if (suggestedLocation && suggestedLocation.value) {
            const {
                value,
                slug,
                currentPlace,
                currentPlaceParent,
                latitude,
                longitude,
                placeType,
                isOnline = false,
            } = suggestedLocation;

            let {placeId} = suggestedLocation;

            placeId = placeId || value;

            // The online destination search endpoint needs the place id to be null
            if (slug === ONLINE_EVENTS_LOCATION_SLUG) {
                placeId = null;
            }

            // On selecting an item the autocomplete per a11y standards refocuses
            // the input. So we need to keep track of that in our own state controller.
            this.setState({
                locationQuery: currentPlace,
                selectedLocation: currentPlace,
            }, () => {
                handleLocationSelect({
                    slug,
                    currentPlace,
                    currentPlaceParent,
                    latitude,
                    longitude,
                    placeType,
                    placeId,
                    isOnline,
                });

                this._isItemSelected = false;
            });
        } else if (isUseCurrentLocationEnabled) {
            this._isItemSelected = false;

            this.setState({
                locationQuery: '',
                selectedLocation: '',
            }, () => {
                handleUseCurrentLocation();
            });
        }
    }

    _handleLocationAutocompleteChange = (locationQuery) => {
        this.setState({locationQuery});

        this._requestLocationSuggestions(locationQuery);
    }

    _handleLocationAutocompleteBlur = () => {
        /*
        Previously we were able to assume the initial city was the previous selected one as each selection
        unmounted the component and triggered a new request. Now this is not the case. SO, we need to track
        in state when a selection has been made.
        */
        const {selectedLocation} = this.state;

        this.setState({
            locationQuery: selectedLocation,
            isTyping: false,
        });
    }

    _handleLocationAutocompleteFocus = () => {
        /* The autocomplete will refocus on the input, triggering the handlefocus
        callback when an item is selected. We don't want to clear the input _after_
        a user selected an item but only when initially clicking. At the moment,
        selecting an item triggers a redirect so it will be reset each time. */
        if (!this._isItemSelected) {
            this.setState({
                locationQuery: '',
                isTyping: true,
            });
            //Setting the value state of the autcomplete component does not
            //trigger the onChange callback. So need to manually update the
            //location suggestions in this situation.
            this._requestLocationSuggestions('');
        }
    }

    _requestLocationSuggestions = debounce((locationQuery) => {
        const {handleLocationSuggestionChange} = this.props;

        handleLocationSuggestionChange(locationQuery);
    }, LOCATION_DEBOUNCE_INTERVAL)

    render() {
        const {
            isWaitingForLocation,
            autocompleteProps,
            placeholder,
            disabled,
        } = this.props;
        const {
            locationQuery,
            selectedLocation,
            suggestions,
        } = this.state;

        let value = locationQuery;

        let _placeholder = placeholder || selectedLocation;

        if (isWaitingForLocation) {
            value = '';
            _placeholder = gettext('Finding your location...');
        }

        return (
            <div className="location-autocomplete" data-spec="location-autocomplete">
                <AutocompleteField
                    suggestions={suggestions}
                    value={value}
                    onBlur={this._handleLocationAutocompleteBlur}
                    onChange={this._handleLocationAutocompleteChange}
                    onFocus={this._handleLocationAutocompleteFocus}
                    onSelect={this._handleLocationAutocompleteSelect}
                    isV2={true}
                    placeholder={_placeholder}
                    disabled={disabled || isWaitingForLocation}
                    {...autocompleteProps}
                />
            </div>
        );
    }
}
